import { useTheme, Stack } from "@carrotfertility/carotene-core"
import useGetMemberBenefit from "#/components/carrot-plans/hooks/useGetMemberBenefit"
import { useOptionsForJourneyType } from "#/components/carrot-plans/hooks/useJourneyStageOptions"
import { RoutingFlowTitle, StepLayout, ButtonFooterLayout, ContinueButton } from "#/components/carrot-plans/shared"
import { ChangeJourneyButton } from "#/components/carrot-plans/shared/ChangeJourneyButtton"
import { OtherJourneyStageTextField } from "#/components/carrot-plans/shared/OtherJourneyStageTextField"
import {
  useCheckRoutingInfoQueryCache,
  useUpdateRoutingInfo
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import {
  getStateMachineValue,
  useGetHeadingCopy
} from "#/components/carrot-plans/steps/journey/WhereAreYouInJourneyStep"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { useConvertTranslatedLabel } from "#/services/common-forms/useConvertTranslatedLabel"
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Form } from "@carrotfertility/carotene-core-x"
import { JourneyStage } from "#/components/carrot-plans/types/journeyStage"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"

interface WhereAreYouInJourneyNowContentProps {
  title: string
  radioOptions: { label: string; value: string }[]
  defaultValues: { journeyStage: string; otherJourneyStage?: string }
  onClickContinue: (formValues: { journeyStage: string; otherJourneyStage: string }) => Promise<void>
  header?: string | JSX.Element
  description?: string | JSX.Element | null
}

function WhereAreYouInJourneyNowContent({
  title,
  radioOptions,
  defaultValues,
  onClickContinue,
  header,
  description
}: WhereAreYouInJourneyNowContentProps): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()

  return (
    <>
      <RoutingFlowTitle title={title} />
      <StepLayout
        header={header || title}
        description={description}
        formElements={
          <>
            <ChangeJourneyButton />
            <Form defaultValues={defaultValues} onSubmit={onClickContinue}>
              <Stack spacing={theme.spacing(theme.tokens.spacing.sm)}>
                <FormRadioButtonGroup
                  name="journeyStage"
                  options={radioOptions}
                  controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
                  aria-labelledby="journeyStage-label"
                />
                <OtherJourneyStageTextField />
              </Stack>
              <ButtonFooterLayout
                continueButton={
                  <ContinueButton>
                    <FormattedMessage defaultMessage="Continue" />
                  </ContinueButton>
                }
              />
            </Form>
          </>
        }
      />
    </>
  )
}

/**
 * This step is for members who have stated that they are no longer in the same journey phase as when they registered.
 * It has to be distinct from the WhereAreYouInJourneyStep in order to avoid an infinite loop in the workflow steps.
 */
export function WhereAreYouInYourJourneyNowStep(): JSX.Element {
  const { data: memberBenefit, isLoading: isMemberBenefitLoading } = useGetMemberBenefit()
  const intl = useIntl()
  const routingData = useCheckRoutingInfoQueryCache()
  const memberJourneyQuery = useGetMemberJourneyData()
  const registrationJourneyResult = memberJourneyQuery.data?.registrationJourney
  const registrationJourneyStage = registrationJourneyResult?.journeyStage
  const defaultValue = routingData?.journeyStage
  const { send } = useStateMachine(getStateMachineValue(defaultValue))
  const { mutateAsync } = useUpdateRoutingInfo()
  const { stageOptions } = useOptionsForJourneyType(routingData?.journey)

  const stageOptionsWithoutRegJourneyStage = stageOptions.filter((stage) => stage.value !== registrationJourneyStage)

  const radioOptions = useConvertTranslatedLabel(stageOptionsWithoutRegJourneyStage).map(({ label, value }) => {
    return {
      label,
      value: value as string
    }
  })

  const { header, supportingCopy } = useGetHeadingCopy({
    memberBenefit,
    journey: routingData?.journey,
    numberOfChildren: routingData?.numberOfChildren,
    journeyStageCheckInPostRegistration: true
  })

  async function onClickContinue(formValues: { journeyStage: string; otherJourneyStage: string }): Promise<void> {
    await mutateAsync(formValues, { onSuccess: () => send(getStateMachineValue(formValues.journeyStage)) })
  }

  const title = intl.formatMessage({ defaultMessage: "Where are you in this journey now?" })

  if (isMemberBenefitLoading) {
    return <CenteredLoadingIndicator />
  }

  return (
    <WhereAreYouInJourneyNowContent
      title={title}
      radioOptions={radioOptions}
      defaultValues={{ journeyStage: defaultValue, otherJourneyStage: routingData?.otherJourneyStage }}
      onClickContinue={onClickContinue}
      header={header}
      description={supportingCopy}
    />
  )
}

/**
 * This step is for members who enter the check-in experience and have stated that they are no longer in the same journey phase
 * as their last completed routing intake.
 */
export function WhereAreYouInYourJourneyNowCheckInStep(): JSX.Element {
  const intl = useIntl()
  const { mutateAsync } = useUpdateRoutingInfo()
  const memberJourneyQuery = useGetMemberJourneyData()
  const routingInfo = useCheckRoutingInfoQueryCache()
  const hasInProgressJourneyStage = Boolean(routingInfo?.journeyStage)
  const isOtherWithUserInput = routingInfo?.journeyStage === JourneyStage.OTHER_WITH_USER_INPUT
  const { send } = useStateMachine(
    hasInProgressJourneyStage ? (isOtherWithUserInput ? "OTHER_WITH_USER_INPUT" : "") : null
  )

  // Journey Data
  const journey = routingInfo?.journey || memberJourneyQuery.data?.currentMemberJourney.withSubmittedRouting.journey
  const journeyStage = routingInfo?.journeyStage
  const otherJourneyStage = routingInfo?.otherJourneyStage
  const journeyStagesNeedingPreservationStep = [
    JourneyStage.PRESERVATION_READY,
    JourneyStage.PRESERVATION_STARTED,
    JourneyStage.PRESERVATION_RECENTLY_RECEIVED
  ]

  const { stageOptions } = useOptionsForJourneyType(journey)
  const radioOptions = useConvertTranslatedLabel(stageOptions).map(({ label, value }) => {
    return {
      label,
      value: value as string
    }
  })

  // Note: This mutation is responsible for updating both the Journey and the JourneyStage values
  async function onClickContinue(formValues: { journeyStage: string; otherJourneyStage: string }): Promise<void> {
    await mutateAsync(
      {
        journey,
        journeyStage: formValues.journeyStage,
        otherJourneyStage: formValues.otherJourneyStage
      },
      {
        onSuccess: () => {
          if (journeyStagesNeedingPreservationStep.includes(formValues.journeyStage as JourneyStage)) {
            send("PRESERVATION")
          } else if (formValues.journeyStage === JourneyStage.OTHER_WITH_USER_INPUT) {
            send("OTHER_WITH_USER_INPUT")
          } else {
            send("")
          }
        }
      }
    )
  }

  const title = intl.formatMessage({ defaultMessage: "Where are you in your journey now?" })

  if (memberJourneyQuery.isLoading) {
    return <CenteredLoadingIndicator />
  }

  return (
    <WhereAreYouInJourneyNowContent
      title={title}
      radioOptions={radioOptions}
      defaultValues={{ journeyStage, otherJourneyStage }}
      onClickContinue={onClickContinue}
    />
  )
}
